import {convertTo14DigitFormat} from '@priority-software/priority-utils/lib/date';
import {priority} from '@priority-software/priority-proto-api/lib';
import {DateEnum} from '../../AppSearches/types/filters';
import {Column, Form, formsStore} from '../../../stores/Form';
import {fieldValue} from '../../../stores/Form/Field';
import {formatTimeTo24HourClock} from '../utils/time';
import {stringToOperator} from '../utils/filters';
import {SearchModeData} from '../components/Table/types';
import {fetchFormData} from '../../../services/form/form';
import {filteredConditions, patchFilterToStore, prepareFilterToServer} from './utils/filter';
import {Filter} from '../../../stores/Form/Filter';
import {prefetchSubformData} from './form';
import {PAGE_LIMIT} from '../consts/pagination';
import {handleScreenMessage} from './screen';
import {PatchFilter} from '../components/AdvancedSearch/AdvancedSearchDrawer';
import {saveRecentSearches, createNewSearch} from './searches';
import {undoAction} from './undo';

export const parseSearchModeData = (form: Form, data: SearchModeData) => {
    if (Object.keys(data).length === 0) {
        return;
    }
    const filter = new Filter({Condition: []});
    const rows = Object.keys(data);
    rows.forEach(rowId => {
        const row = data[rowId];
        const rowIdNum = Number.parseInt(rowId);
        const isOperatorRow = rowIdNum % 2 === 0;
        const columns = Object.keys(row);
        if (isOperatorRow) {
            columns.forEach(columnId => {
                const operatorValue = data[rowId][columnId];
                const columnIdNum = Number.parseInt(columnId);
                // if there's an operator value create a filter with a filterValue from the next row
                if (operatorValue?.length) {
                    // get filterValue from the next row
                    const filterValue = data[(rowIdNum + 1).toString()]?.[columnId];
                    const formatedFilterValue = formatFilterValue(form, columnIdNum, filterValue);
                    filter.Condition.push({
                        ColumnID: columnIdNum,
                        Operator: stringToOperator(operatorValue),
                        Values: [formatedFilterValue || ''],
                        IgnoreCase: false
                    });
                }
            });
        } else {
            columns.forEach(columnId => {
                const filterValue = data[rowId][columnId];
                const columnIdNum = Number.parseInt(columnId);

                //get operatorValue from the previous row
                const operatorValue = data[(rowIdNum - 1).toString()]?.[columnId];

                // if operatorValue isn't empty, then it was already added while parsing the previous row
                if (filterValue && !operatorValue?.length) {
                    filter.Condition.push({
                        ColumnID: columnIdNum,
                        Operator: priority.netitems.shared.ComparisonOperator.Eq,
                        Values: [formatFilterValue(form, columnIdNum, filterValue)],
                        IgnoreCase: false
                    });
                }
            });
        }
    });
    form.addFilter(filter);
};

type ApplyFiltersArgs = {
    form: Form;
    top?: number;
    skip?: number;
    filtersOperator?: priority.netitems.shared.LogicalOperator;
    focusRowIndex?: number;
    filters?: Filter[];
};

export const applyFilters = async (
    args: ApplyFiltersArgs,
    resetHiddenRows = true,
    ignoreSubform = false
): Promise<Form> => {
    const {filtersOperator, skip, form, focusRowIndex} = args;
    const top = form?.maxFetch || args.top || PAGE_LIMIT;
    if (resetHiddenRows) {
        form.resetHiddenRecordKeys();
    }
    const filtersToApply = args.filters || form.filters;
    if (filtersToApply?.length) {
        const {session, sort} = args.form;
        const preparedFilters = prepareFilterToServer(args.form, filtersToApply);
        const data: priority.netitems.form.FetchRs = await fetchFormData({
            session,
            filters: preparedFilters,
            filtersOperator,
            sort,
            top: top ? top + 1 : top,
            skip
        });

        const isActionAllowed = await handleScreenMessage(data);

        if (isActionAllowed) {
            if (!skip) {
                // reset the count value of the previous query
                form.setAllRecordsCount(0);
            }

            // TODO:RemoveAfterBEReady - Since BE pagination is not ready, we increment limit value to simulate pagination behavior
            const hasMore = data?.Records?.length > top;
            const records = top ? data?.Records?.slice(0, top) : data?.Records || [];

            form.setRecords(records, skip, hasMore);
            form.setCurrentRow(focusRowIndex || 0);
            form.setSearchModeLevel(0);

            if (!ignoreSubform) {
                if (data?.Records?.length > 0) {
                    await prefetchSubformData(form);
                } else {
                    const {activeSubForm} = form;
                    if (activeSubForm) {
                        //if filter result empty we should clear activeSubform records, filter and sort
                        activeSubForm.resetRecords();
                        activeSubForm.resetFilters();
                        activeSubForm.resetSort();
                    }
                }
            }

            form.tableAPI?.focusCell(form.currentColumn?.index || 0, 0);
        }
    } else {
        form.resetRecords();
    }
    return form;
};

export const runFiltersFromForm = async (currentForm: Form, searchModeLevel: number) => {
    if (searchModeLevel) {
        const searchData = currentForm.searchModeData;
        parseSearchModeData(currentForm, searchData);

        await applyFilters({form: currentForm});
        createNewSearch(currentForm);
        await saveRecentSearches(currentForm);
        currentForm?.setSearchModeLevel(0);
        currentForm.clearSearchModeData();
    }
};

export const formatFilterValue = (form: Form, columnId: number, value: fieldValue): string => {
    const column: Column = form.getColumnByID(columnId);
    if (column.Type === priority.netitems.form.ColumnType.Date14) {
        return convertTo14DigitFormat(value as string);
    }
    if (column.Type === priority.netitems.form.ColumnType.Time5) {
        return formatTimeTo24HourClock(value as string);
    }
    return value === true ? 'Y' : (value as string);
};

export const addAndApplyFilters = async (
    form: Form,
    filterParams: Array<{
        fieldId: number;
        value: string | string[];
        isIgnoreCase?: boolean;
        operator?: priority.netitems.shared.ComparisonOperator;
    }>,
    top?: number,
    saveSearch = true
) => {
    addFilters(form, filterParams);
    saveSearch && (await saveRecentSearches(form));
    return await applyFilters({
        form,
        top,
        filtersOperator: priority.netitems.shared.LogicalOperator.And
    });
};

export const addFilters = (
    form: Form,
    filterParams: Array<{
        fieldId: number;
        value: string | string[];
        isIgnoreCase?: boolean;
        operator?: priority.netitems.shared.ComparisonOperator;
    }>
) => {
    const filters = createFiltersFromFilterParams(filterParams);
    form?.updateFilters(filters);
};

export const createFiltersFromFilterParams = (
    filterParams: Array<{
        fieldId: number;
        value: string | string[];
        isIgnoreCase?: boolean;
        operator?: priority.netitems.shared.ComparisonOperator;
        dateEnum?: DateEnum[];
    }>
) => {
    const filters = [];
    filterParams.forEach(param => {
        const filter = new Filter();
        filter.Condition = [
            {
                ColumnID: param.fieldId,
                Values: Array.isArray(param.value) ? param.value : [param.value],
                Operator: param?.operator,
                IgnoreCase: !!param?.isIgnoreCase,
                DateEnum: param?.dateEnum
            }
        ];
        filters.push(filter);
    });
    return filters;
};

export const updateColumnFilter = async (form: Form, filter: PatchFilter) => {
    const newFilterIdx = form.filters.findIndex(
        f => f?.Condition[0]?.ColumnID === filter.Condition[0]?.columnID
    );
    let updatedFilters = [];
    if (newFilterIdx === -1) {
        const filteredCondition = filteredConditions(filter.Condition);
        updatedFilters = filteredCondition.length
            ? form.filters.concat(patchFilterToStore({...filter, Condition: filteredCondition}))
            : form.filters;
    } else {
        updatedFilters = form.filters.reduce((acc, filterItem, idx) => {
            if (idx === newFilterIdx) {
                const filteredCondition = filteredConditions(filter.Condition);
                filteredCondition.length &&
                    acc.push(patchFilterToStore({...filter, Condition: filteredCondition}));
            } else {
                acc.push(filterItem);
            }
            return acc;
        }, []);
    }

    form.updateFilters(updatedFilters);
    createNewSearch(form);
    form.searchStore.setCurrentSearchId(null);

    if (updatedFilters.length) {
        await saveRecentSearches(form);
        return await applyFilters({
            form,
            filtersOperator: priority.netitems.shared.LogicalOperator.And
        });
    }
    form.resetSort();
    return form.resetRecords();
};

export const addSort = async (form: Form, sortParams: priority.netitems.form.ISort) => {
    if (form.records.length > 2) {
        form.setSort(new priority.netitems.form.Sort(sortParams));
        await saveRecentSearches(form);
        return await applyFilters({form});
    }
    return form;
};

export const runSearchMode = (currentForm: Form, searchLevel) => {
    currentForm.resetSort();
    currentForm.resetHiddenRecordKeys();

    !searchLevel && currentForm.resetFilters();
    if (searchLevel) {
        const searchData = currentForm.searchModeData;
        parseSearchModeData(currentForm, searchData);
    }
    currentForm.clearSearchModeData();
    formsStore.currentScreen?.toggleSearchMode(currentForm.searchModeLevel + 1);
};

export const clearFormData = async (form: Form) => {
    form.resetFilters();
    form.resetSort();
    form.resetRecords();
    form.setCurrentRow(0);
    form.setCurrentColumnId(1);
    await undoAction(false);
};

export const clearOneRow = async (form: Form) => {
    const currentRecord = form.records[form.currentRow];
    // this check should be enough for Empty rows
    if (currentRecord.isEmpty() && form.records.length >= 1) {
        if (form.currentRow !== form.records.length - 1) {
            form.deleteClientRecord(form.currentRow);
        }
        form.setCurrentRow(Math.max(form.currentRow - 1, 0));
        return;
    }

    await undoAction(false);

    const {activeSubForm} = form;

    form.setHiddenRecordKeys(currentRecord.keys);

    if (form.allRecordsCount) {
        form.setAllRecordsCount(form.allRecordsCount - 1);
    }

    if (activeSubForm) {
        activeSubForm.resetRecords();
        activeSubForm.resetFilters();
        activeSubForm.resetSort();
    }

    if (form.hasMore) {
        const data: priority.netitems.form.FetchRs = await fetchFormData({
            session: form.session,
            filters: form.filters,
            filtersOperator: form.filters[0].Operator,
            sort: form.sort,
            top: 2,
            skip: form.paginationOffset + PAGE_LIMIT + form.hiddenRecords.length - 1
        });

        const isActionAllowed = await handleScreenMessage(data);

        if (isActionAllowed) {
            const hasMore = data?.Records?.length > 1;
            form.deleteClientRecord(form.currentRow);
            const lastRecord = form.records[form.records.length - 1];
            if (lastRecord.isNew) {
                //Remove last empty record for correct merge records process
                form.deleteClientRecord(form.records.length - 1);
            }
            form.mergeRecords(form.records, [data.Records[0]]);
            form.setHasMore(hasMore);

            form.tableAPI?.focusCell(form.currentColumn?.index || 0, form.currentRow);
        }
    } else {
        form.deleteClientRecord(form.currentRow);
    }
    form.setCurrentRow(form.records.length < form.currentRow ? 1 : form.currentRow);

    form.insertEmptyRecord(form.records.length);
};
